import moment from "moment";
import { useEffect } from "react";
import { Col, Row } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useCreateEvChargerScheduleMutation } from "../../../../../../redux/api/ev-charger/evChargerAPI";
import { EChargingMethod } from "../../../../../../shared/oversight-core/enums/charging-method";
import { IHttpError } from "../../../../../../shared/oversight-core/interfaces/http-errror";
import AppSelect, {
  Option,
} from "../../../../../../shared/oversight-core/ui-elements/app-select/app-select";
import AppInput from "../../../../../../shared/oversight-core/ui-elements/input/app-input";
import ModalContainer, {
  ModelContainerProps,
} from "../../../../../../shared/oversight-core/ui-elements/modal-container/modal-container";
import { mergeDateAndTime } from "../../../../../../shared/oversight-core/utils/time-utils";
import {
  showErrorMessage,
  showSuccessMessage,
} from "../../../../../../shared/oversight-core/utils/toast";

interface IAddScheduleModalProps extends ModelContainerProps {
  show: boolean;
  onRefetch: () => void;
  evChargerId: string;
}

interface IFormInput {
  name: string;
  startTime: string;
  endTime: string;
  type: Option | [];
}

const defaultFormValues: IFormInput = {
  name: "",
  startTime: "",
  endTime: "",
  type: [],
};

const AddUpdateScheduleModal = (props: IAddScheduleModalProps) => {
  const { show, evChargerId, onRefetch, ...rest } = props;

  const {
    register,
    handleSubmit,
    reset,
    getValues,
    formState: { errors },
    control,
  } = useForm<IFormInput>({
    defaultValues: { ...defaultFormValues },
  });

  useEffect(() => {
    if (show) {
      reset({ ...defaultFormValues });
    }
  }, [show, reset]);

  const [createSchedule, { isLoading }] = useCreateEvChargerScheduleMutation();

  const handleConfirm = handleSubmit((data) => {
    if (!evChargerId) return;

    createSchedule({
      evChargerId: evChargerId,
      ocppTagId: null,
      connectorId: null,
      scheduleType: (data.type as Option).value,
      repeatMode: "DAILY",
      scheduledPeriods: [
        {
          from: moment(
            mergeDateAndTime(new Date(), data.startTime),
          ).toISOString(true),
          to: moment(mergeDateAndTime(new Date(), data.endTime)).toISOString(
            true,
          ),
        },
      ],
      targetEnergyKwh: null,
      targetSocPercent: null,
      vehicleProfileId: null,
      startingSocPercent: null,
      maxCurrentAmps: null,
      expireDate: null,
      name: data.name.trim(),
    })
      .unwrap()
      .then(() => {
        showSuccessMessage("Schedule created successfully");
        onRefetch();
      })
      .catch((err: IHttpError) => {
        showErrorMessage(err?.message || "Failed to create schedule");
      });
  });

  const typeOptions: Option[] = [
    { value: EChargingMethod.SMART_CHARGE, label: "Smart Charge" },
    { value: EChargingMethod.INSTANT, label: "Instant Charging" },
    { value: EChargingMethod.GREEN_MODE, label: "Green Mode" },
  ];

  return (
    <ModalContainer
      show={show}
      title="Add Schedule"
      onConfirm={handleConfirm}
      confirmButtonText="Confirm"
      cancelButtonText="Cancel"
      confirmButtonVariant="blue"
      cancelButtonVariant="transparent"
      size="modal-md"
      isLoading={isLoading}
      {...rest}
    >
      <Row className="gy-3">
        <Col xs={12}>
          <AppInput
            label="Name"
            placeholder="Name"
            name="name"
            register={register("name", {
              required: "Name is required",
            })}
            errors={errors}
            isRequired={true}
          />
        </Col>
        <Col xs={6}>
          <AppInput
            type="time"
            label="Start Time"
            placeholder="Select Start Time"
            name="startTime"
            register={register("startTime", {
              required: "Start time is required",
            })}
            errors={errors}
            isRequired={true}
          />
        </Col>
        <Col xs={6}>
          <AppInput
            type="time"
            label="End Time"
            placeholder="Select End Time"
            name="endTime"
            register={register("endTime", {
              required: "End time is required",
              validate: (value) => {
                const startTime = getValues("startTime");
                if (startTime && value) {
                  if (
                    moment(value, "HH:mm").isSameOrBefore(
                      moment(startTime, "HH:mm"),
                    )
                  ) {
                    return "End time must be after start time";
                  }
                }
                return true;
              },
            })}
            errors={errors}
            isRequired={true}
          />
        </Col>
        <Col xs={12}>
          <AppSelect
            label="Type"
            options={typeOptions}
            placeholder="Select Type"
            control={control}
            name="type"
            errors={errors}
            register={register("type", {
              required: "Type is required",
            })}
            isRequired={true}
            defaultValue={""}
          />
        </Col>
      </Row>
    </ModalContainer>
  );
};

export default AddUpdateScheduleModal;
